import React from "react";
import { STAGE_META, SOURCE_LABEL } from "@/lib/api";
import { Loader2, Sparkles } from "lucide-react";

const NEW = "__new__";

const Group = ({ title, testId, items, value, onChange, options, newLabel }) => (
  <div data-testid={testId} className="min-w-0">
    <div className="label-caps mb-2">{title}</div>
    <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
      {items.map(([raw, n]) => (
        <div key={raw} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] items-center gap-2">
          <div className="text-xs text-forest truncate" title={raw}>{raw} <span className="text-forest/40">· {n}</span></div>
          <select data-testid={`${testId}-${raw}`} value={value[raw] ?? ""} onChange={(e) => onChange(raw, e.target.value)}
            className={`h-8 border rounded-sm px-1.5 text-xs bg-white min-w-0 ${value[raw] === NEW ? "border-clay text-clay font-semibold" : "border-[#E6E4DD] text-forest"}`}>
            {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            {newLabel && <option value={NEW}>+ New {newLabel}: {raw}</option>}
          </select>
        </div>
      ))}
    </div>
  </div>
);

// Shows each distinct status / source / owner in the file with the suggested Propzel match.
export const ValueReview = ({ values, valueMap, setValueMap, users, state }) => {
  const set = (group) => (raw, v) => setValueMap((p) => ({ ...p, [group]: { ...p[group], [raw]: v } }));
  if (!values.stages.length && !values.sources.length && !values.owners.length) return null;
  return (
    <div data-testid="import-value-review" className="border border-[#E6E4DD] bg-white rounded-sm">
      <div className="px-4 py-3 border-b border-[#E6E4DD] flex flex-wrap items-center justify-between gap-2">
        <div className="label-caps">Review how file values will be saved</div>
        <div data-testid="import-values-status" className="text-[11px] text-forest/60 inline-flex items-center gap-1.5">
          {state === "loading" ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding closest matches…</>
            : state === "ai" ? <><Sparkles className="h-3.5 w-3.5 text-clay" /> Suggested by AI — change any you disagree with</> : "Suggested by rules — change any you disagree with"}
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4">
        {values.stages.length > 0 && <Group title="Lead status → stage" testId="review-stage" items={values.stages} value={valueMap.stages} onChange={set("stages")}
          options={STAGE_META.map((s) => [s.key, s.label])} newLabel="stage" />}
        {values.sources.length > 0 && <Group title="Source" testId="review-source" items={values.sources} value={valueMap.sources} onChange={set("sources")}
          options={Object.entries(SOURCE_LABEL)} newLabel="source" />}
        {values.owners.length > 0 && <Group title="Lead owner" testId="review-owner" items={values.owners} value={valueMap.owners} onChange={set("owners")}
          options={[["", "No match → admin's bucket"], ...users.map((u) => [u.id, `${u.name} (${u.role})`])]} />}
      </div>
      <div className="px-4 pb-3 text-[11px] text-forest/50">New stages and sources are added for this organisation only. New stages stay open in the pipeline and show as their own column on the Leads board.</div>
    </div>
  );
};
